<script lang="ts">
  /**
   * Left flyout drawer — holds projection / view / tier filter / edges /
   * find / depth controls (used to be a top toolbar) PLUS a scrollable
   * Memories list with per-id hide / highlight / color overrides.
   *
   * Toggled by the hamburger button in the top status bar (StatusBar.svelte).
   * Persists open across re-renders. Closes on Escape, click-outside, or
   * the X button.
   */

  import { onMount } from 'svelte'
  import {
    drawer, filters, selection, overrides,
    toggleHidden, toggleHighlighted, setCustomColor, clearAllOverrides,
    bulkHighlight, bulkHide, bulkColor,
    passesFilter,
    ingestQueue, ingestQueueHasActive, loadIngestQueue,
    renameIngestJob, deleteIngestJob, filesPanel,
  } from './selection.svelte'
  import { apiStatus, type LayoutPoint } from './api.svelte'
  import { writeMode } from './selection.svelte'
  import { mco } from './mcoAuth.svelte'

  function fmtBytes(b: number): string {
    if (b < 1024) return `${b} B`
    if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`
    return `${(b / (1024 * 1024)).toFixed(2)} MB`
  }

  function fmtAge(iso: string | null): string {
    if (!iso) return ''
    const sec = Math.floor((Date.now() - new Date(iso).getTime()) / 1000)
    if (sec < 60) return `${sec}s ago`
    if (sec < 3600) return `${Math.floor(sec / 60)}m ago`
    if (sec < 86400) return `${Math.floor(sec / 3600)}h ago`
    return `${Math.floor(sec / 86400)}d ago`
  }

  /* Poll the ingest queue while the Settings OR Files tab is open. 3s
   * cadence when any job is queued/running, 15s when everything is idle.
   * Cancels when the drawer closes or switches to a tab that doesn't
   * surface the queue. */
  let ingestPollTimer: ReturnType<typeof setInterval> | null = null
  $effect(() => {
    const surfaced = drawer.open && (drawer.tab === 'settings' || drawer.tab === 'files')
    if (!surfaced) {
      if (ingestPollTimer) { clearInterval(ingestPollTimer); ingestPollTimer = null }
      return
    }
    void loadIngestQueue()
    const tick = (): void => {
      void loadIngestQueue()
      const cadence = ingestQueueHasActive() ? 3000 : 15000
      if (ingestPollTimer) clearInterval(ingestPollTimer)
      ingestPollTimer = setInterval(tick, cadence)
    }
    ingestPollTimer = setInterval(tick, 3000)
    return () => {
      if (ingestPollTimer) { clearInterval(ingestPollTimer); ingestPollTimer = null }
    }
  })

  /* UPLOADS panel — per-row edit state. Only one row in edit mode at a
   * time (clicking Edit on another row commits/cancels the previous). */
  let editingJobId = $state<string | null>(null)
  let editingTopic = $state('')
  let editError = $state<string | null>(null)
  let deleteConfirmId = $state<string | null>(null)
  let deleteError = $state<string | null>(null)

  function startEdit(jobId: string, currentTopic: string): void {
    editingJobId = jobId
    editingTopic = currentTopic || ''
    editError = null
  }
  function cancelEdit(): void {
    editingJobId = null
    editingTopic = ''
    editError = null
  }
  async function commitEdit(jobId: string): Promise<void> {
    const next = editingTopic.trim()
    if (!next) { editError = 'topic cannot be empty'; return }
    try {
      await renameIngestJob(jobId, next)
      cancelEdit()
    } catch (err) {
      editError = (err as Error).message
    }
  }
  async function confirmDelete(jobId: string): Promise<void> {
    try {
      await deleteIngestJob(jobId)
      deleteConfirmId = null
      deleteError = null
    } catch (err) {
      deleteError = (err as Error).message
    }
  }

  type GroupBy = 'none' | 'topic' | 'agent' | 'tier' | 'document'
  let groupBy = $state<GroupBy>('none')
  let expanded = $state(new Set<string>())  /* group keys the user expanded; default = all COLLAPSED (headers only), so a large group count never renders every member at once */

  type Props = { points: LayoutPoint[] }
  let { points }: Props = $props()

  const limits = [1000, 2500, 5000, 10000, 20000, 50000]
  const strengthOptions = [0.0, 0.25, 0.5, 0.75]

  /** Local search for the memory list (independent from filters.search
   * which affects the constellation). */
  let listSearch = $state('')

  /** Memory list, filtered + sorted by importance — FULL set (uncapped).
   *  Shared by the flat view (which caps at 500 for render perf) and the grouped
   *  view (which must see every row so every document/group appears, not just
   *  those whose chunks rank in the top 500 by importance). */
  const filteredSorted = $derived.by(() => {
    void overrides.hidden.size
    const q = listSearch.trim().toLowerCase()
    const filtered = points.filter((p) => {
      if (!passesFilter(p, filters)) return false
      if (!q) return true
      return (p.topic ?? '').toLowerCase().includes(q) || String(p.id) === q
    })
    filtered.sort((a, b) => {
      const ah = overrides.highlighted.has(a.id) ? 1 : 0
      const bh = overrides.highlighted.has(b.id) ? 1 : 0
      if (ah !== bh) return bh - ah
      const ai = a.importance ?? 0
      const bi = b.importance ?? 0
      if (ai !== bi) return bi - ai
      return b.id - a.id
    })
    return filtered
  })

  /** Flat (ungrouped) view — capped at 500 rows for render perf. */
  const listed = $derived.by(() => filteredSorted.slice(0, 500))

  /** Per-group member-row render cap. Group HEADERS always show the true member
   *  count; only this many rows render when a group is expanded, so opening a
   *  big document doesn't paint thousands of rows. */
  const GROUP_MEMBER_CAP = 100

  /** Group key derivation per row. Topic strips trailing "(Part X/Y)" so
   * chunked-import siblings collapse into one group. Tier maps importance
   * to bucket label. Agent uses raw agent_id (or "(none)"). */
  function keyFor(p: LayoutPoint): string {
    if (groupBy === 'topic') {
      const topic = (p.topic ?? '(no topic)').trim()
      const stripped = topic.replace(/\s*\(?(Part\s+)?(\d+)\s*[\/of]+\s*(\d+)\)?\s*$/i, '').trim()
      return stripped || topic
    }
    if (groupBy === 'document') {
      /* Chunks of an ingested document — sharing a document_id, or carrying
       * a "(Part N/M)" topic — collapse under the document's name (the topic
       * stem). Every other memory lands in one "individual memories" bucket. */
      const topic = (p.topic ?? '').trim()
      const stripped = topic.replace(/\s*\(?(Part\s+)?(\d+)\s*[\/of]+\s*(\d+)\)?\s*$/i, '').trim()
      if (p.document_id || (stripped && stripped !== topic)) {
        return stripped || topic || '(untitled document)'
      }
      return '◦ individual memories'
    }
    if (groupBy === 'agent') return p.agent_id ?? '(none)'
    if (groupBy === 'tier') {
      const i = p.importance ?? 0
      if (i >= 20) return 'APEX (≥20)'
      if (i >= 15) return 'ELITE (15-19)'
      if (i >= 5)  return 'HIGH (5-14)'
      if (i >= 0)  return 'BASE (0-4)'
      return 'LOW (<0)'
    }
    return ''
  }

  /** Grouped view: Map<groupKey, members[]> sorted by group size desc. */
  const grouped = $derived.by(() => {
    if (groupBy === 'none') return null
    void overrides.hidden.size
    const map = new Map<string, LayoutPoint[]>()
    for (const p of filteredSorted) {        /* full set, not the 500-row flat cap */
      const key = keyFor(p)
      let arr = map.get(key)
      if (!arr) { arr = []; map.set(key, arr) }
      arr.push(p)
    }
    /* Sort: groups with more members first; same size → alpha. */
    return Array.from(map.entries())
      .sort((a, b) => b[1].length - a[1].length || a[0].localeCompare(b[0]))
  })

  function toggleExpanded(key: string): void {
    const next = new Set(expanded)
    if (next.has(key)) next.delete(key); else next.add(key)
    expanded = next
  }
  function collapseAll(): void { expanded = new Set() }
  function expandAll(): void {
    if (!grouped) return
    expanded = new Set(grouped.map(([k]) => k))
  }

  /** Bulk action: pick a color for the entire group via a hidden <input
   * type="color">, then apply to all member ids. */
  function applyGroupColor(members: LayoutPoint[]): void {
    const inp = document.createElement('input')
    inp.type = 'color'
    inp.value = '#FFB000'
    inp.oninput = () => {
      bulkColor(members.map((m) => m.id), hexToRgb(inp.value))
    }
    inp.click()
  }

  function close(): void {
    drawer.open = false
  }

  function onKey(e: KeyboardEvent): void {
    if (drawer.open && e.key === 'Escape') {
      e.preventDefault()
      close()
    }
  }

  onMount(() => {
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  })

  function rgbToHex([r, g, b]: [number, number, number]): string {
    const h = (n: number) => Math.max(0, Math.min(255, n)).toString(16).padStart(2, '0')
    return `#${h(r)}${h(g)}${h(b)}`
  }
  function hexToRgb(hex: string): [number, number, number] {
    const m = hex.match(/^#([0-9a-fA-F]{6})$/)
    if (!m) return [255, 176, 0]
    const v = parseInt(m[1], 16)
    return [(v >> 16) & 0xff, (v >> 8) & 0xff, v & 0xff]
  }

  function tierLabel(n: number | null | undefined): string {
    const v = n ?? 0
    if (v >= 20) return 'AP'
    if (v >= 15) return 'EL'
    if (v >= 5)  return 'HI'
    if (v >= 0)  return 'BS'
    return 'LO'
  }
</script>

{#if drawer.open}
  <div class="dr-backdrop" role="presentation" onclick={(e) => { if (e.target === e.currentTarget) close() }}>
    <aside class="dr" aria-label="Controls drawer">
      <header class="dr-head">
        <div class="dr-tabs">
          <button class="dr-tab" class:on={drawer.tab === 'filters'} type="button" onclick={() => drawer.tab = 'filters'}>FILTERS</button>
          <button class="dr-tab" class:on={drawer.tab === 'memories'} type="button" onclick={() => drawer.tab = 'memories'}>
            MEMORIES <span class="dr-count">{points.length.toLocaleString()}</span>
          </button>
          <button class="dr-tab" class:on={drawer.tab === 'files'} type="button" onclick={() => drawer.tab = 'files'}>
            FILES{#if ingestQueue.jobs.length > 0}<span class="dr-count">{ingestQueue.jobs.length}</span>{/if}
          </button>
          <button class="dr-tab" class:on={drawer.tab === 'settings'} type="button" onclick={() => drawer.tab = 'settings'}>SETTINGS</button>
        </div>
        <button class="dr-close" type="button" onclick={close} aria-label="Close drawer">✕</button>
      </header>

      {#if drawer.tab === 'filters'}
        <div class="dr-body dr-form">
          <label class="dr-field">
            <span class="dr-k">projection</span>
            <!-- Beta: PCA only. UMAP/t-SNE/force are live-compute and
                 too slow at scale; re-enabled with the streaming
                 projection endpoint (mu-platform track). -->
            <select class="dr-sel" bind:value={filters.projection}>
              <option value="pca">PCA</option>
            </select>
          </label>

          <label class="dr-field">
            <span class="dr-k">view mode</span>
            <div class="dr-row">
              <button class="dr-btn" class:on={filters.viewMode === '2d'} type="button" onclick={() => filters.viewMode = '2d'}>2D</button>
              <button class="dr-btn" class:on={filters.viewMode === '3d'} type="button" onclick={() => filters.viewMode = '3d'}>3D</button>
              <button class="dr-btn dr-btn-narrow" class:on={filters.zAmplify} type="button" onclick={() => filters.zAmplify = !filters.zAmplify} title="Z-amplify in 3D mode">{filters.zAmplify ? 'AMP' : 'FLAT'}</button>
            </div>
          </label>

          <label class="dr-field">
            <span class="dr-k">tier</span>
            <select class="dr-sel" bind:value={filters.tier}>
              <option value="all">ALL</option>
              <option value="apex">APEX ≥20</option>
              <option value="elite">ELITE 15-19</option>
              <option value="high">HIGH 5-14</option>
              <option value="baseline">BASE 0-4</option>
            </select>
          </label>

          <label class="dr-field">
            <span class="dr-k">limit</span>
            <select class="dr-sel" bind:value={filters.limit}>
              {#each limits as n (n)}
                <option value={n}>{n.toLocaleString()}</option>
              {/each}
            </select>
          </label>

          <label class="dr-field">
            <span class="dr-k">edges</span>
            <select class="dr-sel" bind:value={filters.edgeMode}>
              <option value="none">NONE</option>
              <option value="selected">SELECTED</option>
              <option value="elite">ELITE ≥15</option>
              <option value="apex">APEX ≥20</option>
              <option value="all">ALL</option>
            </select>
            {#if filters.edgeMode !== 'none' && filters.edgeMode !== 'selected'}
              <select class="dr-sel" bind:value={filters.edgeStrengthMin}>
                {#each strengthOptions as s (s)}
                  <option value={s}>strength ≥{s.toFixed(2)}</option>
                {/each}
              </select>
            {/if}
          </label>

          <label class="dr-field">
            <span class="dr-k">find (constellation)</span>
            <input class="dr-input" type="search" placeholder="topic substring" bind:value={filters.search} autocomplete="off" spellcheck="false" />
          </label>

          <div class="dr-field">
            <span class="dr-k">camera</span>
            <div class="dr-row">
              <button
                class="dr-btn"
                class:on={filters.orbit}
                type="button"
                onclick={() => {
                  /* Auto-promote to 3D when activating orbit so the user
                   * doesn't need a second click. Doesn't auto-revert on
                   * deactivate — leaving 3D set is the right default. */
                  if (!filters.orbit && filters.viewMode !== '3d') {
                    filters.viewMode = '3d'
                  }
                  filters.orbit = !filters.orbit
                }}
                title="Auto-rotate the camera around the current selection (auto-switches to 3D)"
              >ORBIT</button>
              <button
                class="dr-btn"
                class:on={filters.walk}
                type="button"
                onclick={() => filters.walk = !filters.walk}
                title="Auto-advance trail every N seconds (composes with ORBIT)"
              >WALK</button>
              {#if filters.orbit || filters.walk}
                <button
                  class="dr-btn dr-btn-narrow"
                  type="button"
                  onclick={() => { filters.orbit = false; filters.walk = false }}
                  title="Stop both camera modes"
                >■ STOP</button>
              {/if}
            </div>
            {#if filters.orbit && filters.viewMode !== '3d'}
              <span class="dr-warn">Orbit only animates in 3D — switch view above</span>
            {/if}
          </div>

          {#if filters.orbit}
            <div class="dr-field dr-field-sub">
              <span class="dr-k">orbit · speed</span>
              <div class="dr-row">
                <button class="dr-btn dr-btn-narrow" class:on={filters.orbitSpeed === 0.1} type="button" onclick={() => filters.orbitSpeed = 0.1}>SLOW</button>
                <button class="dr-btn dr-btn-narrow" class:on={filters.orbitSpeed === 0.4} type="button" onclick={() => filters.orbitSpeed = 0.4}>MED</button>
                <button class="dr-btn dr-btn-narrow" class:on={filters.orbitSpeed === 1.0} type="button" onclick={() => filters.orbitSpeed = 1.0}>FAST</button>
                <button class="dr-btn dr-btn-narrow" class:on={filters.orbitSpeed === 2.5} type="button" onclick={() => filters.orbitSpeed = 2.5}>BLAZE</button>
              </div>
            </div>

            <div class="dr-field dr-field-sub">
              <span class="dr-k">orbit · direction</span>
              <div class="dr-row">
                <button class="dr-btn dr-btn-narrow" class:on={filters.orbitDirection === 1} type="button" onclick={() => filters.orbitDirection = 1} title="Clockwise">↻ CW</button>
                <button class="dr-btn dr-btn-narrow" class:on={filters.orbitDirection === -1} type="button" onclick={() => filters.orbitDirection = -1} title="Counter-clockwise">↺ CCW</button>
                <button
                  class="dr-btn dr-btn-narrow"
                  type="button"
                  onclick={() => filters.orbitDirection = (filters.orbitDirection === 1 ? -1 : 1) as 1 | -1}
                  title="Flip"
                >⇄</button>
              </div>
            </div>

            <div class="dr-field dr-field-sub">
              <span class="dr-k">orbit · pitch ({filters.orbitPitch}°)</span>
              <input
                class="dr-slider"
                type="range"
                min="0"
                max="75"
                step="1"
                bind:value={filters.orbitPitch}
                disabled={filters.orbitAutoTilt}
              />
              <div class="dr-row" style="margin-top:4px">
                <button
                  class="dr-btn dr-btn-narrow"
                  class:on={filters.orbitAutoTilt}
                  type="button"
                  onclick={() => filters.orbitAutoTilt = !filters.orbitAutoTilt}
                  title="Slowly oscillate pitch (cinematic ±15° wobble)"
                >AUTO-TILT</button>
              </div>
            </div>
          {/if}

          {#if filters.walk}
            <div class="dr-field dr-field-sub">
              <span class="dr-k">walk · step interval</span>
              <div class="dr-row">
                <button class="dr-btn dr-btn-narrow" class:on={filters.walkIntervalMs === 1500} type="button" onclick={() => filters.walkIntervalMs = 1500}>1.5s</button>
                <button class="dr-btn dr-btn-narrow" class:on={filters.walkIntervalMs === 3000} type="button" onclick={() => filters.walkIntervalMs = 3000}>3s</button>
                <button class="dr-btn dr-btn-narrow" class:on={filters.walkIntervalMs === 5000} type="button" onclick={() => filters.walkIntervalMs = 5000}>5s</button>
                <button class="dr-btn dr-btn-narrow" class:on={filters.walkIntervalMs === 10000} type="button" onclick={() => filters.walkIntervalMs = 10000}>10s</button>
              </div>
            </div>
          {/if}
        </div>

      {:else if drawer.tab === 'memories'}
        <div class="dr-body dr-list-wrap">
          <div class="dr-list-tools">
            <input class="dr-input dr-list-search" type="search" placeholder="filter list…" bind:value={listSearch} />
            <span class="dr-list-stat">{listed.length.toLocaleString()} / {points.length.toLocaleString()}</span>
            {#if overrides.hidden.size + overrides.highlighted.size + overrides.customColors.size > 0}
              <button class="dr-btn dr-btn-narrow" type="button" onclick={() => clearAllOverrides()} title="Clear all overrides">CLR</button>
            {/if}
          </div>

          <div class="dr-list-tools">
            <span class="dr-k">group by</span>
            <select class="dr-sel" bind:value={groupBy}>
              <option value="none">FLAT</option>
              <option value="document">DOCUMENT</option>
              <option value="topic">TOPIC</option>
              <option value="agent">AGENT</option>
              <option value="tier">TIER</option>
            </select>
            {#if groupBy !== 'none' && grouped && grouped.length > 0}
              <button class="dr-btn dr-btn-narrow" type="button" onclick={expandAll} title="Expand all groups">+</button>
              <button class="dr-btn dr-btn-narrow" type="button" onclick={collapseAll} title="Collapse all groups">−</button>
              <span class="dr-list-stat">{grouped.length} groups</span>
            {/if}
          </div>

          <div class="dr-list">
            {#if groupBy !== 'none' && grouped}
              {#each grouped as [key, members] (key)}
                {@const isExpanded = expanded.has(key)}
                {@const allHidden = members.every((m) => overrides.hidden.has(m.id))}
                {@const allHighlighted = members.every((m) => overrides.highlighted.has(m.id))}
                <div class="dr-group">
                  <header class="dr-group-head">
                    <button
                      class="dr-group-toggle"
                      type="button"
                      onclick={() => toggleExpanded(key)}
                      title={isExpanded ? 'Collapse' : 'Expand'}
                    >{isExpanded ? '▾' : '▸'}</button>
                    <span class="dr-group-name" title={key}>{key}</span>
                    <span class="dr-group-count">{members.length}</span>
                    <div class="dr-group-actions">
                      <button
                        class="dr-row-act"
                        class:on={allHighlighted}
                        type="button"
                        onclick={() => bulkHighlight(members.map((m) => m.id), !allHighlighted)}
                        title="{allHighlighted ? 'Unhighlight' : 'Highlight'} all in group"
                      >★</button>
                      <button
                        class="dr-row-act"
                        type="button"
                        onclick={() => applyGroupColor(members)}
                        title="Recolor entire group"
                      >🎨</button>
                      <button
                        class="dr-row-act"
                        type="button"
                        onclick={() => bulkColor(members.map((m) => m.id), null)}
                        title="Reset group colors"
                      >∅</button>
                      <button
                        class="dr-row-act"
                        class:on={allHidden}
                        type="button"
                        onclick={() => bulkHide(members.map((m) => m.id), !allHidden)}
                        title="{allHidden ? 'Show' : 'Hide'} all in group"
                      >{allHidden ? '✕' : '◉'}</button>
                    </div>
                  </header>
                  {#if isExpanded}
                    <div class="dr-group-members">
                      {#each members.slice(0, GROUP_MEMBER_CAP) as p (p.id)}
                        {@const isHidden = overrides.hidden.has(p.id)}
                        {@const isHighlighted = overrides.highlighted.has(p.id)}
                        {@const customColor = overrides.customColors.get(p.id)}
                        <div class="dr-row" class:hidden={isHidden} class:highlighted={isHighlighted} class:selected={selection.selectedId === p.id}>
                          <button
                            class="dr-row-main"
                            type="button"
                            onclick={() => selection.selectedId = p.id}
                          >
                            <span class="dr-row-tier" data-tier={tierLabel(p.importance).toLowerCase()}>{tierLabel(p.importance)}</span>
                            <span class="dr-row-id">#{p.id}</span>
                            <span class="dr-row-topic">{p.topic ?? '(no topic)'}</span>
                          </button>
                          <button class="dr-row-act" class:on={isHighlighted} type="button" onclick={() => toggleHighlighted(p.id)} title="Highlight (one)">★</button>
                          <input
                            class="dr-row-color"
                            type="color"
                            value={customColor ? rgbToHex(customColor) : '#FFB000'}
                            oninput={(e) => setCustomColor(p.id, hexToRgb((e.currentTarget as HTMLInputElement).value))}
                            oncontextmenu={(e) => { e.preventDefault(); setCustomColor(p.id, null) }}
                          />
                          <button class="dr-row-act" class:on={isHidden} type="button" onclick={() => toggleHidden(p.id)} title="Hide (one)">{isHidden ? '✕' : '◉'}</button>
                        </div>
                      {/each}
                      {#if members.length > GROUP_MEMBER_CAP}
                        <div class="dr-group-more">+{(members.length - GROUP_MEMBER_CAP).toLocaleString()} more in this group — use “filter list…” to narrow</div>
                      {/if}
                    </div>
                  {/if}
                </div>
              {/each}
              {#if grouped.length === 0}
                <div class="dr-empty">— no groups —</div>
              {/if}
            {:else}
            {#each listed as p (p.id)}
              {@const isHidden = overrides.hidden.has(p.id)}
              {@const isHighlighted = overrides.highlighted.has(p.id)}
              {@const customColor = overrides.customColors.get(p.id)}
              <div class="dr-row" class:hidden={isHidden} class:highlighted={isHighlighted} class:selected={selection.selectedId === p.id}>
                <button
                  class="dr-row-main"
                  type="button"
                  onclick={() => selection.selectedId = p.id}
                  title="Select #{p.id}"
                >
                  <span class="dr-row-tier" data-tier={tierLabel(p.importance).toLowerCase()}>{tierLabel(p.importance)}</span>
                  <span class="dr-row-id">#{p.id}</span>
                  <span class="dr-row-topic">{p.topic ?? '(no topic)'}</span>
                </button>
                <button
                  class="dr-row-act"
                  class:on={isHighlighted}
                  type="button"
                  onclick={() => toggleHighlighted(p.id)}
                  title={isHighlighted ? 'Unhighlight' : 'Highlight'}
                >★</button>
                <input
                  class="dr-row-color"
                  type="color"
                  value={customColor ? rgbToHex(customColor) : '#FFB000'}
                  oninput={(e) => setCustomColor(p.id, hexToRgb((e.currentTarget as HTMLInputElement).value))}
                  oncontextmenu={(e) => { e.preventDefault(); setCustomColor(p.id, null) }}
                  title="Click to recolor · right-click to reset"
                />
                <button
                  class="dr-row-act"
                  class:on={isHidden}
                  type="button"
                  onclick={() => toggleHidden(p.id)}
                  title={isHidden ? 'Show' : 'Hide'}
                >{isHidden ? '✕' : '◉'}</button>
              </div>
            {/each}
            {#if listed.length === 0}
              <div class="dr-empty">No memories match the current filters.</div>
            {/if}
            {#if listed.length === 500 && points.length > 500}
              <div class="dr-empty">— list capped at 500 · narrow with the search above —</div>
            {/if}
            {/if}
          </div>
        </div>

      {:else if drawer.tab === 'files'}
        <div class="dr-body dr-form">
          <div class="dr-tabs dr-subtabs">
            <button class="dr-tab" class:on={filesPanel.tab === 'uploads'} type="button" onclick={() => filesPanel.tab = 'uploads'}>
              UPLOADS{#if ingestQueue.jobs.length > 0}<span class="dr-count">{ingestQueue.jobs.length}</span>{/if}
            </button>
            <!-- Future: ATTACHMENTS, MEDIA -->
          </div>

          {#if filesPanel.tab === 'uploads'}
            <div class="dr-field dr-files-actions">
              <button class="dr-btn" type="button" disabled={!writeMode.unlocked} title={writeMode.unlocked ? '' : 'Unlock write mode in Settings first'} onclick={() => { writeMode.ingestDialogOpen = true; close() }}>+ UPLOAD</button>
              {#if ingestQueue.loading}
                <span class="dr-sub">loading…</span>
              {:else if ingestQueue.lastFetchedAt}
                <span class="dr-sub">refreshed {fmtAge(new Date(ingestQueue.lastFetchedAt).toISOString())}</span>
              {/if}
            </div>

            {#if ingestQueue.error}
              <div class="dr-doc-err">{ingestQueue.error}</div>
            {:else if ingestQueue.jobs.length === 0 && !ingestQueue.loading}
              <div class="dr-empty">No uploaded documents yet.</div>
            {:else}
              <ul class="dr-uploads-list">
                {#each ingestQueue.jobs as job (job.job_id)}
                  {@const jumpable = job.head_memory_id != null}
                  {@const isMutating = ingestQueue.mutating.has(job.job_id)}
                  {@const isEditing = editingJobId === job.job_id}
                  {@const isConfirmingDelete = deleteConfirmId === job.job_id}
                  <li class="dr-upload-card" data-status={job.status}>
                    <div class="dr-upload-head">
                      <span class="dr-doc-pill" data-status={job.status}>{job.status.toUpperCase()}</span>
                      {#if isEditing}
                        <input
                          class="dr-upload-edit"
                          type="text"
                          bind:value={editingTopic}
                          maxlength="200"
                          placeholder="document name"
                          onkeydown={(e) => {
                            if (e.key === 'Enter')  { e.preventDefault(); void commitEdit(job.job_id) }
                            if (e.key === 'Escape') { e.preventDefault(); cancelEdit() }
                          }}
                        />
                      {:else}
                        <button
                          class="dr-upload-name"
                          type="button"
                          title={jumpable ? 'Click to jump to first chunk in constellation' : 'No chunks yet'}
                          disabled={!jumpable}
                          onclick={() => { if (jumpable) { selection.selectedId = job.head_memory_id; close() } }}
                        >{job.topic || job.filename || '(untitled)'}</button>
                      {/if}
                    </div>
                    <div class="dr-upload-meta">
                      {#if job.total_chunks}
                        <span>{job.processed_chunks}/{job.total_chunks} chunks</span>
                      {:else}
                        <span>{fmtBytes(job.size_bytes)}</span>
                      {/if}
                      <span>·</span>
                      <span>{fmtAge(job.finished_at ?? job.started_at ?? job.created_at)}</span>
                      {#if job.created_by_subject}
                        <span>·</span>
                        <span class="dr-upload-by">{job.created_by_subject}</span>
                      {/if}
                    </div>
                    {#if job.status === 'failed' && job.error}
                      <div class="dr-doc-error">{job.error.slice(0, 200)}</div>
                    {/if}
                    <div class="dr-upload-actions">
                      {#if isEditing}
                        <button class="dr-btn dr-btn-narrow" type="button" disabled={isMutating} onclick={() => void commitEdit(job.job_id)}>SAVE</button>
                        <button class="dr-btn dr-btn-narrow" type="button" disabled={isMutating} onclick={cancelEdit}>CANCEL</button>
                        {#if editError}<span class="dr-upload-err">{editError}</span>{/if}
                      {:else if isConfirmingDelete}
                        <span class="dr-upload-warn">Delete this document and all {job.total_chunks ?? 0} chunks?</span>
                        <button class="dr-btn dr-btn-narrow dr-btn-danger" type="button" disabled={isMutating} onclick={() => void confirmDelete(job.job_id)}>{isMutating ? 'DELETING…' : 'CONFIRM'}</button>
                        <button class="dr-btn dr-btn-narrow" type="button" disabled={isMutating} onclick={() => { deleteConfirmId = null; deleteError = null }}>CANCEL</button>
                        {#if deleteError}<span class="dr-upload-err">{deleteError}</span>{/if}
                      {:else}
                        <button class="dr-btn dr-btn-narrow" type="button" disabled={isMutating} onclick={() => startEdit(job.job_id, job.topic)}>RENAME</button>
                        <button class="dr-btn dr-btn-narrow dr-btn-danger" type="button" disabled={isMutating} onclick={() => { deleteConfirmId = job.job_id; deleteError = null }}>DELETE</button>
                      {/if}
                    </div>
                  </li>
                {/each}
              </ul>
            {/if}
          {/if}
        </div>

      {:else}
        <div class="dr-body dr-form">
          <div class="dr-field">
            <span class="dr-k">api status</span>
            <div class="dr-row">
              <span class="dr-pill" class:on={apiStatus.online}>
                {apiStatus.online ? 'ONLINE' : 'OFFLINE'}
              </span>
              {#if apiStatus.memoriesWithEmbeddings != null}
                <span class="dr-pill">{apiStatus.memoriesWithEmbeddings.toLocaleString()} mem</span>
              {/if}
              {#if apiStatus.writesEnabled}
                <span class="dr-pill on">WRITES</span>
              {/if}
            </div>
          </div>

          <label class="dr-field">
            <span class="dr-k">write mode</span>
            <div class="dr-row">
              <button
                class="dr-btn"
                class:on={writeMode.unlocked}
                type="button"
                onclick={() => writeMode.unlocked = !writeMode.unlocked}
                disabled={!!mco.viewing}
                title={mco.viewing ? `Viewing ${mco.viewing.name} — read-only` : 'Toggle write lock — arms write actions for this session'}
              >{writeMode.unlocked ? '🔓 UNLOCKED' : '🔒 LOCKED'}</button>
              {#if writeMode.unlocked}
                <button class="dr-btn" type="button" onclick={() => { writeMode.addDialogOpen = true; close() }}>+ NEW MEMORY</button>
                <button class="dr-btn" type="button" onclick={() => { writeMode.ingestDialogOpen = true; close() }}>+ INGEST DOC</button>
              {/if}
            </div>
          </label>

          <div class="dr-field">
            <span class="dr-k">documents</span>
            <div class="dr-row">
              <span class="dr-pill">
                {ingestQueue.jobs.length} upload{ingestQueue.jobs.length === 1 ? '' : 's'}
              </span>
              {#if ingestQueueHasActive()}
                <span class="dr-pill on">{ingestQueue.jobs.filter((j) => j.status === 'queued' || j.status === 'running').length} active</span>
              {/if}
              <button class="dr-btn dr-btn-narrow" type="button" onclick={() => drawer.tab = 'files'}>OPEN FILES →</button>
            </div>
          </div>

          <div class="dr-field dr-help">
            <span class="dr-k">keyboard</span>
            <ul class="dr-help-list">
              <li><kbd>1</kbd>/<kbd>2</kbd>/<kbd>3</kbd>/<kbd>4</kbd> — projection</li>
              <li><kbd>D</kbd> — toggle 2D / 3D</li>
              <li><kbd>E</kbd> — cycle edge mode</li>
              <li><kbd>R</kbd> — reset camera</li>
              <li><kbd>Esc</kbd> — close panel / drawer</li>
            </ul>
          </div>
        </div>
      {/if}
    </aside>
  </div>
{/if}

<style>
  .dr-backdrop {
    position: fixed;
    inset: 0;
    background: rgba(0, 0, 0, 0.6);
    backdrop-filter: blur(2px);
    z-index: 100;
    animation: dr-fade var(--t-mid);
  }
  @keyframes dr-fade { from { opacity: 0 } to { opacity: 1 } }

  .dr {
    position: absolute;
    top: 0;
    left: 0;
    bottom: 0;
    width: min(420px, 90vw);
    background: var(--surface);
    border-right: 1px solid var(--accent-soft);
    box-shadow: 8px 0 32px rgba(0, 0, 0, 0.6);
    display: flex;
    flex-direction: column;
    font-family: var(--font-mono);
    animation: dr-slide var(--t-mid) ease-out;
  }
  @keyframes dr-slide {
    from { transform: translateX(-20%); opacity: 0; }
    to   { transform: translateX(0);    opacity: 1; }
  }

  .dr-head {
    display: flex;
    align-items: center;
    border-bottom: 1px solid var(--outline-variant);
    background: var(--surface-container-low);
    flex-shrink: 0;
  }
  .dr-tabs {
    display: flex;
    flex: 1;
  }
  .dr-tab {
    flex: 1;
    padding: var(--s-2) var(--s-2);
    background: transparent;
    border: none;
    border-bottom: 2px solid transparent;
    color: var(--on-surface-faint);
    font: inherit;
    font-size: 10px;
    letter-spacing: 0.1em;
    cursor: pointer;
    transition: color var(--t-fast), border-color var(--t-fast), background var(--t-fast);
  }
  .dr-tab:hover { color: var(--on-surface); background: var(--surface-container); }
  .dr-tab.on {
    color: var(--accent);
    border-bottom-color: var(--accent);
    background: var(--surface-container);
  }
  .dr-count {
    font-size: 9px;
    color: var(--on-surface-faint);
    margin-left: 4px;
  }
  .dr-tab.on .dr-count { color: var(--accent-bright); }
  .dr-close {
    background: transparent;
    border: 1px solid var(--outline-variant);
    color: var(--on-surface-variant);
    width: 24px; height: 24px;
    margin: var(--s-2) var(--s-2);
    cursor: pointer;
    line-height: 1;
    flex-shrink: 0;
  }
  .dr-close:hover { color: var(--accent); border-color: var(--accent); }

  .dr-body {
    flex: 1;
    overflow-y: auto;
    overscroll-behavior: contain;
  }

  .dr-form {
    display: flex;
    flex-direction: column;
    gap: var(--s-3);
    padding: var(--s-3);
  }
  .dr-field {
    display: flex;
    flex-direction: column;
    gap: 4px;
  }
  .dr-k {
    font-size: 9px;
    color: var(--on-surface-faint);
    letter-spacing: 0.1em;
    text-transform: uppercase;
  }
  .dr-row {
    display: flex;
    gap: 6px;
    align-items: center;
  }
  .dr-sel, .dr-input {
    font-family: inherit;
    font-size: 12px;
    color: var(--on-surface);
    background: var(--surface-container);
    border: 1px solid var(--outline-variant);
    padding: 6px 8px;
    outline: none;
    transition: border-color var(--t-fast);
  }
  .dr-sel:focus, .dr-input:focus { border-color: var(--accent); }

  .dr-btn {
    font-family: inherit;
    font-size: 11px;
    text-transform: uppercase;
    letter-spacing: 0.08em;
    padding: 6px 10px;
    background: transparent;
    border: 1px solid var(--outline-variant);
    color: var(--on-surface-variant);
    cursor: pointer;
    transition: color var(--t-fast), border-color var(--t-fast), background var(--t-fast);
  }
  .dr-btn:hover { color: var(--accent); border-color: var(--accent); }
  .dr-btn.on { color: var(--accent-bright); border-color: var(--accent); background: var(--accent-faint); }
  .dr-btn:disabled { opacity: 0.5; cursor: not-allowed; }
  .dr-btn-narrow { padding: 6px 8px; font-size: 10px; }

  .dr-pill {
    display: inline-block;
    padding: 3px 8px;
    border: 1px solid var(--outline-variant);
    color: var(--on-surface-variant);
    font-size: 10px;
    letter-spacing: 0.06em;
    text-transform: uppercase;
  }
  .dr-pill.on { color: var(--accent); border-color: var(--accent-soft); }

  /* ── Memories tab ─────────────────────────────────────────────── */
  .dr-list-wrap {
    display: flex;
    flex-direction: column;
  }
  .dr-list-tools {
    display: flex;
    gap: 6px;
    align-items: center;
    padding: var(--s-2);
    border-bottom: 1px dashed var(--outline-variant);
    background: var(--surface-container-low);
    flex-shrink: 0;
  }
  .dr-list-search { flex: 1; }
  .dr-list-stat {
    font-size: 9px;
    color: var(--on-surface-faint);
    letter-spacing: 0.06em;
    text-transform: uppercase;
  }
  .dr-list {
    flex: 1;
    overflow-y: auto;
  }
  .dr-row {
    display: grid;
    grid-template-columns: 1fr 28px 22px 28px;
    gap: 4px;
    align-items: center;
    padding: 4px var(--s-2);
    border-bottom: 1px solid var(--outline-variant);
    transition: background var(--t-fast);
  }
  .dr-row:hover { background: var(--surface-container-low); }
  .dr-row.selected { background: var(--accent-faint); }
  .dr-row.hidden { opacity: 0.4; }
  .dr-row.highlighted .dr-row-id { color: var(--accent-bright); }

  .dr-row-main {
    display: flex;
    align-items: center;
    gap: 6px;
    background: transparent;
    border: none;
    color: var(--on-surface);
    font: inherit;
    font-size: 11px;
    text-align: left;
    padding: 0;
    cursor: pointer;
    overflow: hidden;
  }
  .dr-row-tier {
    font-size: 8px;
    padding: 1px 3px;
    border: 1px solid var(--outline-variant);
    color: var(--on-surface-faint);
    letter-spacing: 0.06em;
    flex-shrink: 0;
  }
  .dr-row-tier[data-tier='ap'] { color: #FFCC44; border-color: #FFCC44; }
  .dr-row-tier[data-tier='el'] { color: #FFB000; border-color: #B07000; }
  .dr-row-tier[data-tier='hi'] { color: #E8E8E8; border-color: #444; }
  .dr-row-tier[data-tier='bs'] { color: #888; border-color: #2A2A2A; }
  .dr-row-tier[data-tier='lo'] { color: #FF6B6B; border-color: #FF6B6B; }

  .dr-row-id {
    color: var(--accent);
    font-weight: 600;
    flex-shrink: 0;
  }
  .dr-row-topic {
    color: var(--on-surface);
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  .dr-row-act {
    background: transparent;
    border: 1px solid var(--outline-variant);
    color: var(--on-surface-faint);
    font-size: 12px;
    padding: 2px 4px;
    cursor: pointer;
    transition: color var(--t-fast), border-color var(--t-fast);
  }
  .dr-row-act:hover { color: var(--accent); border-color: var(--accent); }
  .dr-row-act.on { color: var(--accent-bright); border-color: var(--accent); }
  .dr-row-color {
    width: 22px;
    height: 22px;
    border: 1px solid var(--outline-variant);
    background: transparent;
    cursor: pointer;
    padding: 0;
  }
  .dr-empty {
    padding: var(--s-4);
    text-align: center;
    color: var(--on-surface-faint);
    font-size: 11px;
    letter-spacing: 0.08em;
    text-transform: uppercase;
  }

  /* ── Documents pill (used in FILES tab + UPLOADS list) ────────── */
  .dr-doc-pill {
    font-family: var(--font-mono);
    font-size: 9px;
    letter-spacing: 0.08em;
    padding: 2px 6px;
    border: 1px solid var(--outline-variant);
    color: var(--on-surface-variant);
    text-transform: uppercase;
    white-space: nowrap;
  }
  .dr-doc-pill[data-status="queued"]  { color: var(--on-surface-faint); }
  .dr-doc-pill[data-status="running"] { color: var(--accent); border-color: var(--accent-soft); }
  .dr-doc-pill[data-status="done"]    { color: var(--accent-bright); border-color: var(--accent); }
  .dr-doc-pill[data-status="failed"]  { color: #ff6b6b; border-color: rgba(255, 107, 107, 0.4); }
  .dr-doc-error {
    grid-column: 1 / -1;
    font-size: 10px;
    color: #ff6b6b;
    font-family: var(--font-mono);
    padding-left: 6px;
    border-top: 1px dashed rgba(255, 107, 107, 0.3);
    padding-top: 2px;
    margin-top: 2px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  .dr-doc-err {
    color: #ff6b6b;
    font-size: 10px;
    padding: 4px 6px;
    border: 1px dashed rgba(255, 107, 107, 0.4);
    background: rgba(255, 107, 107, 0.05);
    font-family: var(--font-mono);
  }
  .dr-sub {
    color: var(--on-surface-faint);
    text-transform: none;
    letter-spacing: 0;
    font-weight: normal;
  }

  /* ── FILES tab — sub-tabs + UPLOADS list ─────────────────────── */
  .dr-subtabs {
    margin-bottom: var(--s-2);
  }
  .dr-files-actions {
    display: flex;
    align-items: center;
    gap: var(--s-2);
  }
  .dr-uploads-list {
    list-style: none;
    margin: var(--s-2) 0 0;
    padding: 0;
    display: flex;
    flex-direction: column;
    gap: var(--s-2);
  }
  .dr-upload-card {
    display: flex;
    flex-direction: column;
    gap: 4px;
    padding: var(--s-2);
    border: 1px solid var(--outline-variant);
    background: var(--surface-container-low);
    font-size: 12px;
  }
  .dr-upload-card[data-status="failed"] { border-color: rgba(255, 107, 107, 0.4); }
  .dr-upload-head {
    display: flex;
    align-items: center;
    gap: var(--s-2);
  }
  .dr-upload-name {
    flex: 1;
    text-align: left;
    background: transparent;
    border: none;
    padding: 0;
    color: var(--on-surface);
    font-family: var(--font-mono);
    font-size: 13px;
    cursor: pointer;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    transition: color var(--t-fast);
  }
  .dr-upload-name:hover:not(:disabled) { color: var(--accent); }
  .dr-upload-name:disabled { cursor: default; color: var(--on-surface-variant); }
  .dr-upload-edit {
    flex: 1;
    font-family: var(--font-mono);
    font-size: 13px;
    background: var(--surface);
    border: 1px solid var(--accent);
    color: var(--on-surface);
    padding: 4px 6px;
    outline: none;
  }
  .dr-upload-meta {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 4px;
    font-family: var(--font-mono);
    font-size: 10px;
    color: var(--on-surface-faint);
  }
  .dr-upload-by {
    color: var(--on-surface-variant);
  }
  .dr-upload-actions {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: var(--s-2);
    margin-top: 2px;
  }
  .dr-upload-warn {
    color: var(--on-surface-variant);
    font-size: 11px;
    flex: 1;
  }
  .dr-upload-err {
    color: #ff6b6b;
    font-size: 10px;
    font-family: var(--font-mono);
  }
  .dr-btn-danger {
    color: #ff8b8b;
    border-color: rgba(255, 107, 107, 0.4);
  }
  .dr-btn-danger:hover:not(:disabled) {
    color: #ff6b6b;
    border-color: #ff6b6b;
    background: rgba(255, 107, 107, 0.05);
  }

  /* ── Camera section sub-fields + slider ──────────────────────── */
  .dr-field-sub {
    margin-left: var(--s-3);
    padding-left: var(--s-2);
    border-left: 2px solid var(--accent-soft);
  }
  .dr-warn {
    color: #ff6b6b;
    font-size: 10px;
    letter-spacing: 0.06em;
    text-transform: uppercase;
    margin-top: 2px;
  }
  .dr-slider {
    width: 100%;
    accent-color: var(--accent);
    cursor: pointer;
  }
  .dr-slider:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }

  /* ── Group view ───────────────────────────────────────────────── */
  .dr-group {
    border-bottom: 1px solid var(--outline-variant);
  }
  .dr-group-head {
    display: grid;
    grid-template-columns: 18px 1fr auto auto;
    gap: 6px;
    align-items: center;
    padding: 6px var(--s-2);
    background: var(--surface-container-low);
    border-left: 2px solid var(--accent-soft);
    transition: background var(--t-fast);
  }
  .dr-group-head:hover { background: var(--surface-container); }
  .dr-group-toggle {
    background: transparent;
    border: none;
    color: var(--accent);
    font-size: 11px;
    line-height: 1;
    cursor: pointer;
    padding: 0;
  }
  .dr-group-name {
    color: var(--accent-bright);
    font-size: 11px;
    font-weight: 600;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  .dr-group-count {
    color: var(--on-surface-faint);
    font-size: 9px;
    padding: 1px 5px;
    border: 1px solid var(--outline-variant);
    background: var(--surface);
    letter-spacing: 0.06em;
  }
  .dr-group-actions {
    display: flex;
    gap: 2px;
  }
  .dr-group-members {
    background: var(--surface-container-lowest);
    padding-left: 12px;
  }
  .dr-group-members .dr-row {
    border-left: 1px dashed var(--outline-variant);
  }
  .dr-group-more {
    padding: 6px 12px;
    color: var(--on-surface-faint);
    font-size: 11px;
    font-style: italic;
    border-left: 1px dashed var(--outline-variant);
  }

  /* ── Settings tab ─────────────────────────────────────────────── */
  .dr-help { margin-top: var(--s-3); }
  .dr-help-list {
    list-style: none;
    margin: 0;
    padding: 0;
    font-size: 11px;
    color: var(--on-surface-variant);
    line-height: 1.7;
  }
  .dr-help-list kbd {
    background: var(--surface-container);
    border: 1px solid var(--outline-variant);
    padding: 1px 4px;
    font-size: 9px;
    color: var(--accent);
  }
</style>
